import { getHttpSessionHeaders } from './http-session';
import { HttpError } from './http-error';
import { isPlayground } from '@/utils/check-is-playground';

export { HttpError, isHttpError } from './http-error';
export type { ErrorType } from './http-error';

export type FetchConfig = {
  url?: string;
  method?: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  responseType?: 'json' | 'blob' | 'text';
  timeout?: number;
};

export type FetchMockRequest = {
  url: string;
  method: string;
  data?: unknown;
};

type FetchMockHandler = (request: FetchMockRequest) => unknown | undefined;

let fetchMockHandler: FetchMockHandler | null = null;

export function setFetchMockHandler(handler: FetchMockHandler | null): void {
  fetchMockHandler = handler;
}

function toSearchParams(params?: Record<string, unknown>): string {
  if (!params) return '';

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        search.append(key, String(item));
      }
      continue;
    }
    search.set(key, String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

function applyHeaders(headers: Headers, init?: HeadersInit): void {
  if (!init) return;

  if (init instanceof Headers) {
    init.forEach((value, key) => headers.set(key, value));
    return;
  }

  if (Array.isArray(init)) {
    for (const [key, value] of init) headers.set(key, value);
    return;
  }

  for (const [key, value] of Object.entries(init)) {
    if (value == null) continue;
    headers.set(key, String(value));
  }
}

function buildHeaders(config: FetchConfig, options?: FetchConfig): Headers {
  const headers = new Headers();
  if (!isPlayground()) {
    applyHeaders(headers, getHttpSessionHeaders());
  }
  applyHeaders(headers, config.headers);
  applyHeaders(headers, options?.headers);
  return headers;
}

function buildBody(method: string, data: unknown, headers: Headers): BodyInit | undefined {
  if (data === undefined || method === 'GET' || method === 'HEAD') return undefined;
  if (data instanceof FormData || data instanceof Blob || typeof data === 'string') return data;

  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  return JSON.stringify(data);
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;

  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (!text) return undefined;

  if (contentType.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  return text;
}

function isBlobContentType(contentType: string): boolean {
  const normalized = contentType.toLowerCase();
  if (normalized.includes('json')) return false;

  return (
    normalized.includes('application/octet-stream') ||
    normalized.includes('application/zip') ||
    normalized.includes('application/gzip') ||
    normalized.includes('text/csv') ||
    normalized.includes('application/csv')
  );
}

function shouldReturnBlob(responseType: FetchConfig['responseType'], contentType: string): boolean {
  if (responseType === 'blob') return true;
  if (responseType === 'json' || responseType === 'text') return false;
  return isBlobContentType(contentType);
}

function mergeAbortSignals(signal?: AbortSignal, timeout?: number): AbortSignal | undefined {
  if (timeout == null) return signal;
  const timeoutSignal = AbortSignal.timeout(timeout);
  if (!signal) return timeoutSignal;
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([signal, timeoutSignal]);

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signal.addEventListener('abort', onAbort);
  timeoutSignal.addEventListener('abort', onAbort);
  if (signal.aborted || timeoutSignal.aborted) controller.abort();
  return controller.signal;
}

function sanitizeUrlQueryParams(url: string): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) return url;

  const path = url.slice(0, queryIndex);
  const search = new URLSearchParams(url.slice(queryIndex + 1));

  for (const [key, value] of [...search.entries()]) {
    if (value === 'null') search.delete(key);
  }

  const sanitizedQuery = search.toString();
  return sanitizedQuery ? `${path}?${sanitizedQuery}` : path;
}

function resolveUrl(path: string, params?: Record<string, unknown>): string {
  const base = typeof window !== 'undefined' ? window.X_API_URL : '';
  const url = path.startsWith('http://') || path.startsWith('https://') ? path : `${base ?? ''}${path}`;
  return sanitizeUrlQueryParams(`${url}${toSearchParams(params)}`);
}

function isFetchConfig(value: string | FetchConfig): value is FetchConfig {
  return typeof value === 'object';
}

type FetchClientOptions = RequestInit & {
  params?: Record<string, unknown>;
  data?: unknown;
  responseType?: 'json' | 'blob' | 'text';
  timeout?: number;
};

async function executeFetch<T>(config: FetchConfig): Promise<T> {
  const method = (config.method ?? 'GET').toUpperCase();
  const url = config.url;
  if (!url) throw new Error('Request URL is required');

  const { params, data, responseType, signal, timeout } = config;
  const mergedSignal = mergeAbortSignals(signal, timeout);

  const mock = fetchMockHandler?.({ url, method, data });
  if (mock !== undefined) return mock as T;

  const headers = buildHeaders(config);
  const response = await fetch(resolveUrl(url, params), {
    method,
    headers,
    body: buildBody(method, data, headers),
    signal: mergedSignal,
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (shouldReturnBlob(responseType, contentType)) {
    if (!response.ok) {
      const errorBody = contentType.includes('json') ? await parseResponseBody(response) : await response.text();
      throw new HttpError(response.status, errorBody);
    }
    return (await response.blob()) as T;
  }

  const body = await parseResponseBody(response);
  if (!response.ok) {
    throw new HttpError(response.status, body);
  }

  return body as T;
}

type CustomFetch = {
  <T>(url: string, options?: FetchClientOptions): Promise<T>;
  <T>(config: FetchConfig, options?: FetchConfig): Promise<T>;
};

async function customFetchImpl<T>(
  urlOrConfig: string | FetchConfig,
  options?: FetchClientOptions | FetchConfig,
): Promise<T> {
  if (isFetchConfig(urlOrConfig)) {
    const configOptions = options as FetchConfig | undefined;
    return executeFetch<T>({
      ...urlOrConfig,
      ...configOptions,
      url: configOptions?.url ?? urlOrConfig.url,
      method: configOptions?.method ?? urlOrConfig.method,
      params: configOptions?.params ?? urlOrConfig.params,
      data: configOptions?.data ?? urlOrConfig.data,
      headers: configOptions?.headers ?? urlOrConfig.headers,
      signal: configOptions?.signal ?? urlOrConfig.signal ?? undefined,
      responseType: configOptions?.responseType ?? urlOrConfig.responseType,
      timeout: configOptions?.timeout ?? urlOrConfig.timeout,
    });
  }

  const fetchOptions = options as FetchClientOptions | undefined;
  const body = fetchOptions?.body ?? fetchOptions?.data;

  return executeFetch<T>({
    url: urlOrConfig,
    method: fetchOptions?.method,
    params: fetchOptions?.params,
    data: body,
    headers: fetchOptions?.headers,
    signal: fetchOptions?.signal ?? undefined,
    responseType: fetchOptions?.responseType,
    timeout: fetchOptions?.timeout,
  });
}

export const customFetch: CustomFetch = customFetchImpl;
